import type { ReactNode } from "react";
import { Alert } from "antd";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "@/shared/components";

const Page = styled.main`
	position: relative;
	height: 100vh;
	min-height: 100vh;
	overflow: hidden;
	background: #fdfdfd;
	display: grid;
	grid-template-columns: minmax(0, 1fr) 590px;
	color: #303030;

	&::before {
		content: "";
		position: absolute;
		inset: 0;
		background: url("/assets/creator-register/background.png") center / cover no-repeat;
		opacity: 0.08;
		pointer-events: none;
	}
`;

const MarketingPanel = styled.section`
	position: relative;
	min-height: 100vh;
	display: flex;
	align-items: center;
	padding: 64px clamp(56px, 8.9vw, 128px);
`;

const MarketingContent = styled.div`
	width: min(566px, 100%);
`;

const Wordmark = styled(Link)`
	display: block;
	width: 319px;
	height: 32px;
	margin-bottom: 64px;

	img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: contain;
		object-position: left center;
	}
`;

const HeroTitle = styled.h1`
	font-size: 48px;
	font-weight: 700;
	line-height: 1.5;
	margin: 0 0 12px;

	span {
		display: block;
	}
`;

const HeroCopy = styled.p`
	font-size: 16px;
	line-height: 1.5;
	margin: 0;
	max-width: 566px;
`;

const FormPanel = styled.section<{
	$topPadding?: number;
	$bottomPadding?: number;
	$reserveScrollbarGutter?: boolean;
}>`
	position: relative;
	height: 100vh;
	min-height: 100vh;
	box-sizing: border-box;
	background: #fdfdfd;
	box-shadow: 0 4px 12px rgba(21, 89, 165, 0.04);
	padding: ${({ $topPadding = 80, $bottomPadding = 48 }) => `${$topPadding}px 64px ${$bottomPadding}px`};
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	scrollbar-gutter: ${({ $reserveScrollbarGutter }) => ($reserveScrollbarGutter ? "stable" : "auto")};
`;

const Header = styled.header<{ $marginBottom?: number }>`
	margin-bottom: ${({ $marginBottom = 36 }) => $marginBottom}px;

	@media (max-width: 640px) {
		margin-bottom: ${({ $marginBottom = 28 }) => Math.min($marginBottom, 28)}px;
	}
`;

const Title = styled.h2`
	font-size: 24px;
	font-weight: 600;
	line-height: 1.2;
	margin: 0;
`;

const Subtitle = styled.p`
	font-size: 14px;
	line-height: 24px;
	color: #707070;
	margin: 0;
`;

const BackPosition = styled.div<{ $marginBottom?: number }>`
	margin-bottom: ${({ $marginBottom = 40 }) => $marginBottom}px;
`;

const ResponsiveLayout = styled.div`
	display: contents;

	@media (max-width: 900px) {
		${Page} {
			grid-template-columns: 1fr;
		}
		${MarketingPanel} {
			display: none;
		}
		${FormPanel} {
			width: min(590px, 100%);
			margin: 0 auto;
		}
	}

	@media (max-width: 640px) {
		${FormPanel} {
			padding: 52px 24px 32px;
		}
	}
`;

interface NewAuthLayoutProps {
	title: ReactNode;
	subtitle?: ReactNode;
	error?: string;
	showBack?: boolean;
	onBack?: () => void;
	headerMarginBottom?: number;
	topPadding?: number;
	bottomPadding?: number;
	backMarginBottom?: number;
	backColor?: string;
	hideHeader?: boolean;
	reserveScrollbarGutter?: boolean;
	children: ReactNode;
}

export const NewAuthLayout = ({
	title,
	subtitle,
	error,
	showBack = false,
	onBack,
	headerMarginBottom,
	topPadding,
	bottomPadding,
	backMarginBottom,
	backColor,
	hideHeader = false,
	reserveScrollbarGutter = false,
	children,
}: NewAuthLayoutProps) => (
	<ResponsiveLayout>
		<Page>
			<MarketingPanel aria-label="Creatorverse introduction">
				<MarketingContent>
					<Wordmark to="/" aria-label="Creatorverse home">
						<img src="/Creatorverse_Wordmark_Gradient.svg" alt="Creatorverse" />
					</Wordmark>
					<HeroTitle>
						<span>Connect With the</span>
						<span>Right Creators</span>
						<span>to Grow Your Brand</span>
					</HeroTitle>
					<HeroCopy>
						We make it simple for clients to find creators, collaborate, and launch campaigns without the
						hassle.
					</HeroCopy>
				</MarketingContent>
			</MarketingPanel>
			<FormPanel
				$topPadding={topPadding}
				$bottomPadding={bottomPadding}
				$reserveScrollbarGutter={reserveScrollbarGutter}
			>
				{showBack && onBack && (
					<BackPosition $marginBottom={backMarginBottom}>
						<BackButton
							label="Back"
							onClick={onBack}
							style={backColor ? { color: backColor } : undefined}
						/>
					</BackPosition>
				)}
				{!hideHeader && (
					<Header $marginBottom={headerMarginBottom}>
						<Title>{title}</Title>
						{subtitle && <Subtitle>{subtitle}</Subtitle>}
					</Header>
				)}
				{error && <Alert type="error" showIcon message={error} style={{ marginBottom: 20 }} />}
				{children}
			</FormPanel>
		</Page>
	</ResponsiveLayout>
);
